/**
 * SEO meta + JSON-LD helpers.
 *
 * Each route returns an `SeoMeta` from its `+page.ts` load (or constructs
 * one inline). The root layout reads it and emits the meta tags + JSON-LD
 * inside `<svelte:head>`. This keeps the heavy structured-data block in
 * one place rather than copy-pasted across every page.
 */

import { absoluteUrl, SITE } from './site';

export interface SeoMeta {
	title: string;
	description: string;
	/** Path on this domain, leading slash, trailing slash. */
	path: string;
	/** OG image absolute URL — defaults to /og-image.png. */
	ogImage?: string;
	/** Schema.org type for the JSON-LD WebPage block. */
	pageType?: 'WebPage' | 'AboutPage' | 'CollectionPage' | 'ContactPage';
	/** Optional Article block (for blog posts). */
	article?: {
		datePublished: string; // ISO 8601
		dateModified?: string;
		readingTimeMinutes: number;
	};
	/** Optional list of crawler hints. Defaults to general index/follow. */
	robots?: string;
}

/**
 * Compose the full `<title>` value. Home page omits the suffix; everything
 * else gets " — Ekavi Care".
 */
export function pageTitle(meta: SeoMeta): string {
	if (meta.path === '/') return meta.title;
	return `${meta.title} — ${SITE.name}`;
}

/**
 * The page's canonical absolute URL.
 */
export function canonicalUrl(meta: SeoMeta): string {
	return absoluteUrl(meta.path);
}

export function ogImageUrl(meta: SeoMeta): string {
	return meta.ogImage ?? absoluteUrl('/og-image.png');
}

/**
 * Construct the JSON-LD @graph block. We always emit Organization +
 * Person + WebSite + WebPage. Articles are appended only on essay pages.
 *
 * AI search engines (ChatGPT, Perplexity, Claude, Gemini, Google AI
 * Overviews) lean on @graph relationships heavily for citations; the
 * tight `@id` cross-references below are deliberate.
 */
export function jsonLdGraph(meta: SeoMeta): string {
	const orgId = `${SITE.publisher.url}/#organization`;
	const websiteId = `${absoluteUrl('/')}#website`;
	const pageId = `${canonicalUrl(meta)}#webpage`;

	const graph: Array<Record<string, unknown>> = [
		{
			'@type': 'Organization',
			'@id': orgId,
			name: SITE.publisher.name,
			legalName: SITE.publisher.legalName,
			url: SITE.publisher.url,
			foundingDate: '2026',
			description:
				'Ekavi Care — an AI back-office colleague for independent US ambulatory practices.'
		},
		{
			'@type': 'WebSite',
			'@id': websiteId,
			url: absoluteUrl('/'),
			name: SITE.name,
			description: SITE.description,
			inLanguage: 'en-US',
			publisher: { '@id': orgId },
			author: { '@id': orgId }
		},
		{
			'@type': 'SoftwareApplication',
			'@id': `${absoluteUrl('/')}#software`,
			name: SITE.name,
			alternateName: ['Ekavi'],
			url: absoluteUrl('/'),
			applicationCategory: 'BusinessApplication',
			applicationSubCategory: 'HealthcareApplication',
			operatingSystem: 'Web',
			description:
				"Ekavi Care is an AI back-office colleague for independent US ambulatory practices. It is the orchestration layer that runs the back office as one — across the EHR (athenahealth, eClinicalWorks, Tebra), the clearinghouse and PA networks (Availity, Stedi, Change Healthcare, Cohere Health), the scribe (Abridge, Suki, Ambience, DAX), and the agentic AI Ekavi builds for the rest. The foundation is Ekavi's natural-language fluency in the EHR — the practice manager or a provider says what they need, and Ekavi does it inside the EHR they already use. The other four capability surfaces ride on this: prior authorization + eligibility end-to-end, claims and denial management, charge capture and coding (powered by the practice's scribe), and scheduling + the AI receptionist (with integrated voice agent partners). Administrative-only; clinical decisions remain with the physician. Built for the 2026 HIPAA Security Rule.",
			featureList: [
				"EHR busywork, handled: Ekavi handles the day-to-day work inside the practice's EHR — in-basket triage, refill routing, eligibility data posting, scheduling moves. The practice manager or provider gives plain-English commands; Ekavi asks for consent before anything clinical and logs every action.",
				'Scheduling optimization and the AI receptionist: predicts no-show risk per slot, refills gaps from the waitlist; the integrated AI receptionist (voice and chat) answers calls and routes to the front desk only what humans should handle',
				'Prior authorization and eligibility, end-to-end: orchestrates EHR + clearinghouse + payer FHIR API + portal + voice agent for the residual; PA cycle compresses from eight days to thirty-six hours',
				"Coding and claims, in one pass: sits between the practice's scribe and EHR — extracts ICD-10, CPT, modifier 25, and E&M codes; queues every code for the biller to approve before the claim leaves; files the encounter note; posts the chart updates back. Recovers $150K/yr in revenue typically lost to undercoding. Practices without a scribe get one through Ekavi at preferred terms.",
				'Claims and denial management: clusters identical payer-code denials, drafts the appeal with prior-PA reference and policy citation, sends through the practice clearinghouse, tracks recovery to the dollar',
				'Plugs into the EHR you already use: athenahealth, eClinicalWorks, Tebra',
				'Plugs into the clearinghouse you trust or one we propose: Availity, Stedi, Change Healthcare',
				'Audit log on every action — seven-year retention, CSV or signed PDF export',
				'Annual HIPAA Security Rule verification packet, fixed cadence',
				'BAA written for AI: PHI does not train models, 90-day retention default',
				'Administrative-only — does not make clinical decisions'
			],
			audience: {
				'@type': 'Audience',
				audienceType: 'Independent ambulatory medical practices (2–49 MD groups)'
			},
			author: { '@id': orgId },
			publisher: { '@id': orgId },
			isPartOf: { '@id': websiteId }
		},
		{
			'@type': meta.pageType ?? 'WebPage',
			'@id': pageId,
			url: canonicalUrl(meta),
			name: pageTitle(meta),
			description: meta.description,
			isPartOf: { '@id': websiteId },
			primaryImageOfPage: ogImageUrl(meta),
			inLanguage: 'en-US'
		}
	];

	if (meta.article) {
		graph.push({
			'@type': 'Article',
			'@id': `${canonicalUrl(meta)}#article`,
			headline: meta.title,
			description: meta.description,
			url: canonicalUrl(meta),
			datePublished: meta.article.datePublished,
			dateModified: meta.article.dateModified ?? meta.article.datePublished,
			image: ogImageUrl(meta),
			author: { '@id': orgId },
			publisher: { '@id': orgId },
			mainEntityOfPage: { '@id': pageId },
			inLanguage: 'en-US',
			timeRequired: `PT${meta.article.readingTimeMinutes}M`
		});
	}

	return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}
